var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getPeriod } from "@/core/period/api";
import { HttpClient } from "@/lib/http";
const ENDPOINTS = {
    SEND_CHANGE_REQUEST: "/webapi/objetivos/change-requests/itemized-bulk",
    FETCH_PENDING_CHANGES: "/webapi/objetivos/pending-changes",
    APPROVE_REJECT_CHANGE: "/webapi/objetivos/update-status-requests",
};
function generateChangeRequestId(changeRequest) {
    const { distributor, objectiveId, period } = changeRequest;
    return `${distributor}-${objectiveId}-${period.year}-${period.month}`;
}
export const sendChangeRequest = (changeRequest) => __awaiter(void 0, void 0, void 0, function* () {
    var _a;
    // Prepare payload
    const payload = {
        objetivo_id: changeRequest.objectiveId,
        justificacion: changeRequest.justification,
        ano_natural: changeRequest.period.year,
        mes_natural: changeRequest.period.month,
        items: changeRequest.rows.flatMap((row) => Object.entries(row.fields).map(([fieldKey, newValue]) => {
            const [table, attribute] = fieldKey.split(".");
            return {
                code: row.delegacion_zona_de_ventas,
                attr: attribute,
                new_value: newValue,
                objetivo_attributo: table,
            };
        })),
    };
    // Make API call
    let response = yield HttpClient.post(ENDPOINTS.SEND_CHANGE_REQUEST, payload);
    if ("error" in response) {
        console.warn("Bulk edit failed:", response.error);
        response = {
            message: response.error,
            total_requests: 0,
            successful: 0,
            failed: 1,
        };
    }
    if (response.successful !== changeRequest.rows.length) {
        console.warn(`Bulk edit: Expected ${changeRequest.rows.length} successful edits, got ${response.successful}`);
    }
    if (response.successful !== response.total_requests) {
        console.warn(`Bulk edit: ${response.successful} of ${response.total_requests} succeeded`);
    }
    // Handle errors
    return {
        success: "failed" in response ? response.failed === 0 : true,
        affectedRows: response.successful,
        message: response.message || "Cambios aplicados correctamente",
        errors: ((_a = response.errors) !== null && _a !== void 0 ? _a : []).map(({ error }) => error),
    };
});
export const fetchPendingChanges = (_a) => __awaiter(void 0, [_a], void 0, function* ({ period }) {
    const { month, year } = period || {};
    const response = yield HttpClient.get(ENDPOINTS.FETCH_PENDING_CHANGES, {
        mes_natural: month ? String(month) : undefined,
        ano_natural: year ? String(year) : undefined,
    });
    const data = response.data;
    //sort data by created_at newest first
    data.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    const ChangeRequestsMap = {};
    data.forEach((item) => {
        const period = getPeriod(item.ano_natural, item.mes_natural);
        const changeRequestId = generateChangeRequestId({
            distributor: item.destributor,
            objectiveId: item.objective_id,
            period: period,
        });
        if (!ChangeRequestsMap[changeRequestId]) {
            ChangeRequestsMap[changeRequestId] = {
                id: changeRequestId,
                distributor: item.destributor,
                objectiveId: item.objective_id,
                period: period,
                rows: [],
                changeIds: [],
            };
        }
        const changeRequest = ChangeRequestsMap[changeRequestId];
        // Find or create the row for the delegacion_zona_de_ventas
        let row = changeRequest.rows.find((r) => r.delegacion_zona_de_ventas === item.code);
        if (!row) {
            row = {
                delegacion_zona_de_ventas: item.code,
                fields: {},
            };
            changeRequest.rows.push(row);
        }
        // Add the field change to the row
        const fieldKey = `${item.objectivo_attribute}.${item.attribute}`;
        row.fields[fieldKey] = item.new_value;
        // Track change IDs
        changeRequest.changeIds.push(item.id);
    });
    return Object.values(ChangeRequestsMap);
});
/**
 * Fetch pending changes formatted for table display
 * Returns array of partial rows with changed field values
 */
export const fetchPendingChangesForTable = (_a) => __awaiter(void 0, [_a], void 0, function* ({ period, dataRows, }) {
    const { month, year } = period || {};
    const response = yield HttpClient.get(ENDPOINTS.FETCH_PENDING_CHANGES, {
        mes_natural: month ? String(month) : undefined,
        ano_natural: year ? String(year) : undefined,
    });
    const changes = response.data;
    // Group changes by row identifier
    const changesByRowKey = new Map();
    for (const change of changes) {
        // Create row key for matching
        const rowKey = `${change.destributor}|${change.code}|${change.ano_natural}|${change.mes_natural}`;
        // Get or create the pending change row
        if (!changesByRowKey.has(rowKey)) {
            changesByRowKey.set(rowKey, {
                _row_id: {
                    distributor: change.destributor,
                    delegacion_zona_de_ventas: change.code,
                    ano_natural: change.ano_natural,
                    mes_natural: change.mes_natural,
                },
            });
        }
        const pendingRow = changesByRowKey.get(rowKey);
        // Build the field key (no mapping needed - API keys match table field keys)
        const fieldKey = `${change.objectivo_attribute}.${change.attribute}`;
        pendingRow[fieldKey] = change.new_value;
    }
    return Array.from(changesByRowKey.values());
});
export const approveRejectChange = (resolution) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b, _c, _d;
    const requestsPayload = resolution.changeRequest.changeIds.map((changeId) => ({
        change_id: changeId,
        status: resolution.status,
        comment: resolution.comment,
    }));
    // Make API call to approve/reject changes
    const response = yield HttpClient.post(ENDPOINTS.APPROVE_REJECT_CHANGE, {
        requests: requestsPayload,
    });
    return {
        success: response.failed === 0,
        message: (_d = (_c = (_b = (_a = response.errors) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.error) !== null && _c !== void 0 ? _c : response.message) !== null && _d !== void 0 ? _d : "Change request processed successfully.",
    };
});
